import {useRouter} from 'next/router';
import PropTypes from 'prop-types';
import {useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {RESPONSE_STATUSES} from '@/src/app/constants/api';
import {
  RESULT_DATA_TYPE,
  SEARCH_FILTERS,
  SEARCH_TYPE,
} from '@/src/app/constants/search';
import {
  selectAllCourseCategories,
  setAllCourseCategories,
} from '@/src/app/store/createCourse/courseCategoriesSlice';
import LanguagesFilters from '@/src/Search/components/LanguagesFilters';
import AllCandidatesFilters from '@/src/Search/containers/AllCandidatesFilters';
import ClinicFilters from '@/src/Search/containers/ClinicFilters';
import UsersFilter from '@/src/Search/containers/UsersFilter';
import VacanciesFilter from '@/src/Search/containers/VacanciesFilter';
import TextLocal from '@/src/textLocal/containers/TextLocal';

import {CheckboxCommon, Separator} from '../../ui';
import AvailabilityFilters from '../components/AvailabilityFilters';
import CategoriesFilters from '../components/CategoriesFilters';
import DifficultyLevelFilters from '../components/DifficultyLevelFilters';
import FilterRow from '../components/FilterRow';
import RatingFilters from '../components/RatingFilters';
import SortingFilters from '../components/SortingFilters';
import {getCategories, getResultDataType} from '../utils';
import {useSearchFilters} from '../utils/useSearchFilters';
import TargetUserSearchFilter from './TargetUserSearchFilter';

const FilterListContainer = ({userType}) => {
  const courseCategories = useSelector(selectAllCourseCategories);
  const {query, locale} = useRouter();
  const dispatch = useDispatch();
  const showTargetUserSearchFilter = useMemo(() => {
    return (
      Boolean(query[SEARCH_FILTERS.FIRST_NAME]) &&
      Boolean(query[SEARCH_FILTERS.LAST_NAME]) &&
      Boolean(query[SEARCH_FILTERS.USER_ID])
    );
  }, [query]);

  const {setQueryFilters, onFilterClickHandle, onMultipleFilterClickHandle} =
    useSearchFilters(userType);

  useEffect(() => {
    const getCategoriesData = async () => {
      const {responseBody, status} = await getCategories();

      if (status === RESPONSE_STATUSES.SUCCESS) {
        dispatch(setAllCourseCategories(responseBody?.categories || []));
      }
    };

    if (!courseCategories.length) {
      getCategoriesData();
    }
  }, []);

  if (query.slug === SEARCH_TYPE.ALL_CANDIDATES) {
    return (
      <AllCandidatesFilters
        onMultipleFilterClickHandle={onMultipleFilterClickHandle}
      />
    );
  }

  if (
    query.slug === SEARCH_TYPE.ALL_POSITIONS ||
    query.slug === SEARCH_TYPE.CLINIC_VACANCIES ||
    query.slug === SEARCH_TYPE.VACANCIES
  ) {
    return (
      <VacanciesFilter
        withoutClinics={query.slug === SEARCH_TYPE.CLINIC_VACANCIES}
      />
    );
  }

  if (
    getResultDataType(query.slug) === RESULT_DATA_TYPE.COURSE ||
    getResultDataType(query.slug) === RESULT_DATA_TYPE.POSTS
  ) {
    return (
      <>
        {showTargetUserSearchFilter && (
          <>
            <TargetUserSearchFilter query={query} />

            <FilterRow>
              <Separator />
            </FilterRow>
          </>
        )}

        <AvailabilityFilters
          query={query}
          onChangeHandler={onMultipleFilterClickHandle}
        />

        <FilterRow>
          <Separator />
        </FilterRow>

        <SortingFilters query={query} setQueryFilters={setQueryFilters} />

        <FilterRow>
          <Separator />
        </FilterRow>

        <LanguagesFilters
          query={query}
          onChangeHandler={onMultipleFilterClickHandle}
        />

        <FilterRow>
          <Separator />
        </FilterRow>

        <DifficultyLevelFilters
          query={query}
          onChangeHandler={onMultipleFilterClickHandle}
        />

        <FilterRow>
          <Separator />
        </FilterRow>

        <RatingFilters query={query} setQueryFilters={setQueryFilters} />

        <FilterRow>
          <Separator />
        </FilterRow>

        <FilterRow isHighlighted>
          <CheckboxCommon
            id={SEARCH_FILTERS.FILTERS_ORIGINAL}
            name="original"
            label={<TextLocal id={'search.originalContent'} />}
            data-filter-name={SEARCH_FILTERS.FILTERS_ORIGINAL}
            data-value="true"
            checked={!!query[SEARCH_FILTERS.FILTERS_ORIGINAL]}
            onChange={onFilterClickHandle}
            isSemibold
          />
        </FilterRow>

        <CategoriesFilters
          query={query}
          onMultipleFilterClickHandle={onMultipleFilterClickHandle}
          courseCategories={courseCategories}
          locale={locale}
        />
      </>
    );
  }

  if (getResultDataType(query.slug) === RESULT_DATA_TYPE.USER) {
    return (
      <>
        <UsersFilter setQueryFilters={setQueryFilters} userType={userType} />
      </>
    );
  }

  if (getResultDataType(query.slug) === RESULT_DATA_TYPE.FOLLOW) {
    return (
      <>
        <UsersFilter setQueryFilters={setQueryFilters} userType={userType} />
      </>
    );
  }

  if (getResultDataType(query.slug) === RESULT_DATA_TYPE.CLINICS) {
    // @ts-ignore
    return <ClinicFilters userType={userType} />;
  }

  return null;
};

FilterListContainer.propTypes = {
  userType: PropTypes.string,
};

export default FilterListContainer;
